import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { N58Service, N58Health } from '../../services/n58.service';
import { N58SocketService } from '../../services/n58-socket.service';
import { SpinnerService } from 'src/app/shared/services/spinner.service';
import { RegistrarReferenciaOperadorModalComponent } from '../referencias-ves-admin/registrar-referencia-operador-modal/registrar-referencia-operador-modal.component';
import { N58MontoMinModalComponent } from './n58-monto-min-modal/n58-monto-min-modal.component';
import { N58CasosModalComponent } from './n58-casos-modal/n58-casos-modal.component';
import { N58MovementDetailModalComponent } from './n58-movement-detail-modal/n58-movement-detail-modal.component';

@Component({
  selector: 'app-n58-home',
  templateUrl: './n58-home.component.html',
  styleUrls: ['./n58-home.component.sass'],
})
export class N58HomeComponent implements OnInit, OnDestroy {
  health: N58Health | null = null;
  healthLoading = true;
  healthError: string | null = null;

  stats: any = null;
  statsLoading = true;

  movements: any[] = [];
  movementsLoading = false;
  movementsTotal = 0;
  movementsPage = 1;
  movementsPages = 1;
  movementsLimit = 20;

  accounts: any[] = [];
  accountsLoading = true;

  syncing = false;
  socketConnected = false;

  exportingXls = false;

  /**
   * Casos de pago que esperan una decision. El contador va en el header porque
   * el problema que resuelve es que se olviden: un tab habria que acordarse de
   * abrirlo.
   */
  casosAbiertos = 0;
  casosCargando = true;

  filterType = '';
  filterReference = '';
  filterDateFrom = '';
  filterDateTo = '';
  filterConcept = '';

  private sub = new Subscription();

  constructor(
    private n58: N58Service,
    private n58Socket: N58SocketService,
    private spinner: SpinnerService,
    private dialog: MatDialog,
    private snack: MatSnackBar,
  ) {}

  ngOnInit(): void {
    // Carga inicial via REST
    this.loadHealth();
    this.loadAccounts();
    this.loadStats();
    this.loadMovements();
    this.cargarCasosAbiertos();

    // Conectar socket N58 para tiempo real
    this.n58Socket.connect();

    this.sub.add(
      this.n58Socket.isConnected$().subscribe((v) => (this.socketConnected = v)),
    );

    // Health en tiempo real (backend emite cada 30s)
    this.sub.add(
      this.n58Socket.onHealth$().subscribe((h) => {
        this.health = h;
        this.healthLoading = false;
        this.healthError = null;
      }),
    );

    // Nuevo movimiento: prepend a la lista sin recargar (sólo si cumple los filtros activos)
    this.sub.add(
      this.n58Socket.onMovementNew$().subscribe((m) => {
        const exists = this.movements.some((e) => e.id === m.id || e.transactionUuid === m.transactionUuid);
        if (!exists && this.movementsPage === 1 && this.matchesActiveFilters(m)) {
          this.movements = [m, ...this.movements];
          if (this.movements.length > this.movementsLimit) {
            this.movements.pop();
          }
          this.movementsTotal++;
          this.movementsPages = Math.ceil(this.movementsTotal / this.movementsLimit);
        }
      }),
    );

    // Stats actualizados
    this.sub.add(
      this.n58Socket.onMovementStats$().subscribe((s) => {
        this.stats = s;
        this.statsLoading = false;
      }),
    );

    // Sync status
    this.sub.add(
      this.n58Socket.onSyncStatus$().subscribe((s) => {
        this.syncing = s.syncing;
      }),
    );

    // Saldos actualizados desde el backend (cron tras detectar movimientos nuevos)
    this.sub.add(
      this.n58Socket.onAccountsUpdated$().subscribe((accs) => {
        this.accounts = Array.isArray(accs) ? accs : [];
        this.accountsLoading = false;
      }),
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    this.n58Socket.disconnect();
  }

  loadAccounts(): void {
    this.accountsLoading = true;
    this.sub.add(
      this.n58.getProducts('account').subscribe({
        next: (res) => {
          this.accounts = Array.isArray(res) ? res : (res?.data || []);
          this.accountsLoading = false;
        },
        error: () => {
          this.accounts = [];
          this.accountsLoading = false;
        },
      }),
    );
  }

  parseN58Amount(val: string): number {
    if (!val) return 0;
    return parseFloat(val.replace(/\./g, '').replace(',', '.'));
  }

  loadHealth(): void {
    this.sub.add(
      this.n58.getHealth().subscribe({
        next: (h) => {
          this.health = h;
          this.healthLoading = false;
          this.healthError = null;
        },
        error: (err) => {
          this.healthError = typeof err === 'string' ? err : 'Error consultando health';
          this.healthLoading = false;
        },
      }),
    );
  }

  loadStats(): void {
    this.statsLoading = true;
    this.sub.add(
      this.n58.getMovementStats().subscribe({
        next: (s) => {
          this.stats = s;
          this.statsLoading = false;
        },
        error: () => {
          this.stats = null;
          this.statsLoading = false;
        },
      }),
    );
  }

  loadMovements(): void {
    this.movementsLoading = true;
    const params: Record<string, string> = {
      page: this.movementsPage.toString(),
      limit: this.movementsLimit.toString(),
    };
    const ref = (this.filterReference || '').trim();
    const concept = (this.filterConcept || '').trim();
    if (this.filterType) params['type'] = this.filterType;
    if (ref) params['reference'] = ref;
    if (this.filterDateFrom) params['dateFrom'] = this.filterDateFrom;
    if (this.filterDateTo) params['dateTo'] = this.filterDateTo;
    if (concept) params['concept'] = concept;

    this.sub.add(
      this.n58.getMovements(params).subscribe({
        next: (res) => {
          this.movements = res.data || [];
          this.movementsTotal = res.total || 0;
          this.movementsPages = res.pages || 1;
          this.movementsLoading = false;
        },
        error: () => {
          this.movements = [];
          this.movementsLoading = false;
        },
      }),
    );
  }

  applyFilters(): void {
    this.movementsPage = 1;
    this.loadMovements();
  }

  private matchesActiveFilters(m: any): boolean {
    if (this.filterType && m.movementType !== this.filterType) return false;

    const ref = (this.filterReference || '').trim().toLowerCase();
    if (ref && !((m.referenceNumber || '').toLowerCase().includes(ref))) return false;

    const concept = (this.filterConcept || '').trim().toLowerCase();
    if (concept) {
      const haystack = `${m.transactionConcept || ''} ${m.transactionName || ''}`.toLowerCase();
      if (!haystack.includes(concept)) return false;
    }

    // Fechas: el input es día Caracas (yyyy-MM-dd) y m.transactionDate es UTC.
    // Comparamos contra el rango UTC equivalente al día Caracas (offset -04:00).
    if (this.filterDateFrom || this.filterDateTo) {
      const txMs = new Date(m.transactionDate).getTime();
      if (Number.isNaN(txMs)) return false;
      if (this.filterDateFrom) {
        const startMs = Date.parse(`${this.filterDateFrom}T04:00:00.000Z`);
        if (txMs < startMs) return false;
      }
      if (this.filterDateTo) {
        const endDate = new Date(`${this.filterDateTo}T04:00:00.000Z`);
        endDate.setUTCDate(endDate.getUTCDate() + 1);
        if (txMs >= endDate.getTime()) return false;
      }
    }

    return true;
  }

  clearFilters(): void {
    this.filterType = '';
    this.filterReference = '';
    this.filterDateFrom = '';
    this.filterDateTo = '';
    this.filterConcept = '';
    this.movementsPage = 1;
    this.loadMovements();
  }

  /**
   * Descarga los movimientos N58 que matchean los filtros actuales como XLSX.
   * Espejo de Banco Plaza. Si el dataset > 50k filas, el server devuelve 413
   * y mostramos snackbar pidiendo acotar filtros.
   */
  exportarXls(): void {
    if (this.exportingXls) return;
    this.exportingXls = true;
    const params: Record<string, string> = {};
    const ref = (this.filterReference || '').trim();
    const concept = (this.filterConcept || '').trim();
    if (this.filterType) params['type'] = this.filterType;
    if (ref) params['reference'] = ref;
    if (this.filterDateFrom) params['dateFrom'] = this.filterDateFrom;
    if (this.filterDateTo) params['dateTo'] = this.filterDateTo;
    if (concept) params['concept'] = concept;

    this.sub.add(
      this.n58.exportMovements(params).subscribe({
        next: (blob) => {
          this.exportingXls = false;
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
          a.download = `n58-movimientos-${ts}.xlsx`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          this.snack.open('Archivo descargado', 'OK', { duration: 3000 });
        },
        error: (err) => {
          this.exportingXls = false;
          const msg = String(err);
          if (msg.includes('413') || msg.toLowerCase().includes('excede')) {
            this.snack.open(
              'El export excede 50.000 filas — acotá filtros (fecha/tipo/concepto)',
              'OK',
              { duration: 6000 },
            );
          } else {
            this.snack.open(`Error exportando: ${msg}`, 'OK', { duration: 5000 });
          }
        },
      }),
    );
  }

  goPage(page: number): void {
    if (page < 1 || page > this.movementsPages) return;
    this.movementsPage = page;
    this.loadMovements();
  }

  forceSync(): void {
    this.syncing = true;
    this.sub.add(
      this.n58.forceSync().subscribe({
        next: () => {
          this.syncing = false;
          this.loadStats();
          this.loadMovements();
          this.loadAccounts();
        },
        error: () => {
          this.syncing = false;
        },
      }),
    );
  }

  /**
   * Un `reintegro` es el banco devolviendo un payout que no se completó.
   * Llega como crédito, así que sin este caso caía en el `else` del ternario
   * de abajo y se pintaba como Egreso con flecha hacia arriba: plata que
   * entra, mostrada como plata que sale. Reusa el estilo de reverso, que es
   * lo que es.
   */
  getMovementClass(m: any): string {
    const name = (m.transactionName || '').toUpperCase();
    if (m.movementType === 'reintegro') return 'mov-reverso';
    if (name.includes('REV ')) return 'mov-reverso';
    if (m.movementType === 'cargo') return 'mov-cargo';
    if (name.includes('COM.') || name.includes('COM ')) return 'mov-comision';
    return m.movementType === 'payin' ? 'mov-payin' : 'mov-payout';
  }

  getMovementIcon(m: any): string {
    const name = (m.transactionName || '').toUpperCase();
    if (m.movementType === 'reintegro') return 'replay';
    if (name.includes('REV ')) return 'replay';
    if (m.movementType === 'cargo') return 'receipt';
    if (name.includes('COM.') || name.includes('COM ')) return 'receipt';
    return m.movementType === 'payin' ? 'arrow_downward' : 'arrow_upward';
  }

  getMovementBadge(m: any): string {
    const name = (m.transactionName || '').toUpperCase();
    if (m.movementType === 'reintegro') return 'Reintegro';
    if (name.includes('REV ')) return 'Reverso';
    if (m.movementType === 'cargo') return 'Cargo bancario';
    if (name.includes('COM.') || name.includes('COM ')) return 'Comisión';
    return m.movementType === 'payin' ? 'Ingreso' : 'Egreso';
  }

  /**
   * Signo del monto. Existe porque el `payin ? '+' : '-'` que había en la
   * plantilla dejaba a los reintegros con signo negativo: un reintegro es el
   * banco **devolviendo** plata, así que entra, y se mostraba como si saliera.
   *
   * Se decide por lo mismo que el color y el icono, para que las tres cosas no
   * puedan volver a contradecirse: un movimiento pintado como reverso, con
   * icono de reverso, ahora también lleva `+`.
   */
  getAmountSign(m: any): string {
    const entra =
      m.movementType === 'payin' ||
      m.movementType === 'reintegro' ||
      (m.transactionName || '').toUpperCase().includes('REV ');
    return entra ? '+' : '-';
  }

  openRegistrarOperadorModal(): void {
    const isMobile = window.innerWidth < 768;
    const dialogRef = this.dialog.open(RegistrarReferenciaOperadorModalComponent, {
      data: { lockedRail: 'N58' },
      maxWidth: isMobile ? '95vw' : '826px',
      minWidth: isMobile ? '90vw' : '500px',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) this.loadMovements();
    });
  }

  openMontoMinModal(): void {
    const isMobile = window.innerWidth < 768;
    this.dialog.open(N58MontoMinModalComponent, {
      maxWidth: isMobile ? '95vw' : '560px',
      minWidth: isMobile ? '90vw' : '480px',
    });
  }

  /** Solo el contador: la lista la trae el modal cuando se abre. */
  cargarCasosAbiertos(): void {
    this.casosCargando = true;
    this.n58.listarCasos('ABIERTO').subscribe({
      next: (res) => {
        this.casosAbiertos = res?.data?.length ?? 0;
        this.casosCargando = false;
      },
      // Un contador que no carga no puede romper la pantalla: se apaga y ya.
      error: () => {
        this.casosAbiertos = 0;
        this.casosCargando = false;
      },
    });
  }

  openCasosModal(): void {
    const isMobile = window.innerWidth < 768;
    const ref = this.dialog.open(N58CasosModalComponent, {
      maxWidth: isMobile ? '95vw' : '900px',
      minWidth: isMobile ? '92vw' : '700px',
    });
    // Si se cerro algun caso, el contador del header tiene que reflejarlo.
    ref.afterClosed().subscribe((huboCambios) => {
      if (huboCambios) this.cargarCasosAbiertos();
    });
  }

  /**
   * Qué mostrar como contraparte en la fila.
   *
   * Gana el nombre que manda el banco en el `detalle`: es el que el operador
   * reconoce y el único que existe en un payin. El beneficiario de nuestra
   * agenda es el respaldo para los payouts donde el banco no devuelve nombre.
   *
   * Los dos campos vienen ya resueltos del backend; acá sólo se elige.
   */
  getContraparteFila(m: any): string {
    return m?.contraparteNombre || m?.beneficiarioNombre || '';
  }

  /**
   * Estado del payout en las mismas palabras que usa Banco Plaza, para que el
   * operador no tenga que traducir entre dos dashboards.
   *
   * Viene de `n58_transaction_logs`, no de `movement.status`: ese vale
   * siempre `registered` en los payouts y no dice nada.
   */
  getPayoutStatusLabel(m: any): string {
    return (
      {
        success: 'Aprobada',
        failed: 'Rechazada',
        pending: 'Pendiente',
        pending_verification: 'Verificando',
        requires_human_review: 'Revisión manual',
      }[m?.payoutStatus as string] || ''
    );
  }

  /** Colores del chip de estado. Verde sale, rojo no salió, ámbar aún no se sabe. */
  getPayoutStatusClass(m: any): string {
    return (
      {
        success: 'pay-ok',
        failed: 'pay-fail',
        pending: 'pay-wait',
        pending_verification: 'pay-wait',
        requires_human_review: 'pay-review',
      }[m?.payoutStatus as string] || ''
    );
  }

  /**
   * Abre el modal de detalle del movimiento. Espejo del flujo BP
   * (openTransactionDetail). Le pasa el movement completo del listado para
   * que pinte de inmediato; el detalle ampliado —cliente, beneficiario y la
   * versión del banco— lo pide el propio modal.
   */
  openMovementDetail(m: any): void {
    const isMobile = window.innerWidth < 768;
    this.dialog.open(N58MovementDetailModalComponent, {
      data: { movement: m },
      maxWidth: isMobile ? '98vw' : '900px',
      minWidth: isMobile ? '95vw' : '700px',
    });
  }

  get sslDaysClass(): string {
    if (!this.health?.ssl?.daysRemaining) return '';
    if (this.health.ssl.daysRemaining <= 7) return 'ssl-danger';
    if (this.health.ssl.daysRemaining <= 30) return 'ssl-warning';
    return 'ssl-ok';
  }
}
